import { apiFetch } from "@/adapters/api/base";
import type {
  SavedView,
  SavedViewsRepository,
  WriteResult,
} from "@estelle/domain";

/**
 * Saved views, over HTTP, stored in Postgres (FR-VUE-5 … 7).
 *
 * Shared by everyone, so a view saved in one browser is a tab in every other.
 * The API returns the domain shapes; nothing is mapped here.
 */

const BASE = "/api/saved-views";

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Anything else is a
 * transport failure, which does throw.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Saved view ${method} failed: ${String(response.status)} on ${path || "/"}`,
  );
}

export function createApiSavedViews(): SavedViewsRepository {
  return {
    list: async (scope) => {
      const response = await apiFetch(
        `${BASE}?scope=${encodeURIComponent(scope)}`,
      );
      if (!response.ok) {
        throw new Error(
          `Saved views request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as SavedView[];
    },

    create: (input) => write<SavedView>("", "POST", input),

    update: (id, patch) =>
      write<SavedView>(`/${encodeURIComponent(id)}`, "PATCH", patch),

    setDefault: (id) =>
      write<SavedView>(`/${encodeURIComponent(id)}/default`, "POST"),

    delete: async (id) => {
      const result = await write<null>(`/${encodeURIComponent(id)}`, "DELETE");
      return result.ok ? { ok: true, data: undefined } : result;
    },
  };
}
